import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import type { SupportedLocale, InterpolationVars } from './types';
import { createTranslator } from './createTranslator';
import { formatDate as formatDateUtil, formatRelativeTime, monthShorts as monthShortsUtil } from './dates';
import { formatCurrency, formatNumber } from './numbers';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthProvider';

// Lazy-load locale bundles as JSON
const loadLocale = async (locale: SupportedLocale): Promise<any> => {
  switch (locale) {
    case 'en': {
      const mod = await import('./locales/en.json');
      return mod.default;
    }
    case 'lv': {
      const mod = await import('./locales/lv.json');
      return mod.default;
    }
    default: {
      const mod = await import('./locales/en.json');
      return mod.default;
    }
  }
};

const STORAGE_KEY = 'app.locale';
const STORAGE_KEY_CURRENCY = 'app.currency';

// In-memory cache for loaded dictionaries (prime with EN only to avoid flash)
import enDict from './locales/en.json';
let dictionaryCache: Partial<Record<SupportedLocale, any>> = {
  en: enDict,
};

interface I18nContextValue {
  language: SupportedLocale;
  currency: string;
  setLanguage: (lang: SupportedLocale) => void;
  setCurrency: (currencyCode: string) => void;
  t: (key: string, vars?: InterpolationVars) => string;
  formatDate: (date: Date | string | number, formatStr: string) => string;
  formatRelativeTime: (date: Date | string | number) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  monthShorts: () => string[];
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

/**
 * Detect initial locale from localStorage, browser, or default to 'en'
 * Note: Database preference is loaded in useEffect after user is available
 * Guarded against SSR/pre-hydration access to window/localStorage
 */
function detectInitialLocale(): SupportedLocale {
  try {
    if (typeof window !== 'undefined') {
      // Check localStorage first (will be overridden by database if user is logged in)
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'en' || stored === 'lv') {
        return stored;
      }

      // Check browser language
      const browserLang = navigator.language || (navigator as any).userLanguage;
      if (browserLang && browserLang.toLowerCase().startsWith('lv')) {
        return 'lv';
      }
    }
  } catch (error) {
    // Silently fallback if localStorage/navigator access fails
    console.warn('[i18n] Failed to detect locale, defaulting to en:', error);
  }

  // Default to English
  return 'en';
}

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { user } = useAuth();
  const initialLocale = detectInitialLocale();
  const [language, setLanguageState] = useState<SupportedLocale>(initialLocale);
  const [languageLoaded, setLanguageLoaded] = useState(false);
  // Start with EN dict as fallback, update when target locale loads
  const [dict, setDict] = useState<any>(dictionaryCache.en);

  // Load language + currency preference from database when user is available
  useEffect(() => {
    const loadPrefsFromDatabase = async () => {
      if (!user?.id || languageLoaded) return;

      try {
        const { data, error } = await supabase
          .from('users')
          .select('preferred_language, preferred_currency')
          .eq('id', user.id)
          .maybeSingle();

        if (!error && data?.preferred_language && (data.preferred_language === 'en' || data.preferred_language === 'lv')) {
          setLanguageState(data.preferred_language as SupportedLocale);
          // Also update localStorage for consistency
          try {
            if (typeof window !== 'undefined') {
              localStorage.setItem(STORAGE_KEY, data.preferred_language);
            }
          } catch (e) {
            // Ignore localStorage errors
          }
        }

        if (!error && data?.preferred_currency && typeof data.preferred_currency === 'string') {
          setCurrencyState(data.preferred_currency);
        }
        setLanguageLoaded(true);
      } catch (error) {
        console.warn('[i18n] Failed to load language from database, using localStorage:', error);
        setLanguageLoaded(true);
      }
    };

    loadPrefsFromDatabase();
  }, [user?.id, languageLoaded]);

  // Preload selected locale on first paint if not EN
  useEffect(() => {
    if (!dictionaryCache[language]) {
      loadLocale(language).then((loadedDict) => {
        dictionaryCache[language] = loadedDict;
        setDict(loadedDict);
      });
    } else {
      setDict(dictionaryCache[language]);
    }
  }, [language]);

  // Create translator function with locale for ICU MessageFormat and EN fallback
  const t = useMemo(() => {
    const fallbackDict = dictionaryCache.en || enDict;
    return createTranslator(dict, language, fallbackDict);
  }, [dict, language]);

  // Set language and persist to localStorage and database (guarded against SSR)
  const setLanguage = async (lang: SupportedLocale) => {
    setLanguageState(lang);
    
    // Save to localStorage (always, as fallback)
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, lang);
      }
    } catch (error) {
      console.warn('[i18n] Failed to persist language preference to localStorage:', error);
    }

    // Save to database if user is logged in
    if (user?.id) {
      try {
        const { error } = await supabase
          .from('users')
          .update({ preferred_language: lang })
          .eq('id', user.id);

        if (error) {
          console.warn('[i18n] Failed to save language preference to database:', error);
        }
      } catch (error) {
        console.warn('[i18n] Failed to save language preference to database:', error);
      }
    }
  };

  // Sync HTML lang attribute and document direction
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
      // For future RTL support: document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
      
      // Dev logging for testing
      if (import.meta.env.DEV) {
        console.log(`[i18n] Language changed to: ${language}`);
        console.log(`[i18n] HTML lang attribute set to: ${document.documentElement.lang}`);
      }
    }
  }, [language]);

  // Format date with current locale
  const formatDate = (date: Date | string | number, formatStr: string) => {
    return formatDateUtil(date, formatStr, language);
  };

  // Format relative time with current locale
  const formatRelativeTimeWithLocale = (date: Date | string | number) => {
    return formatRelativeTime(date, language);
  };

  // Currency for current user/session (DB-backed preference; defaults to EUR, with localStorage cache)
  const [currency, setCurrencyState] = useState<string>(() => {
    try {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem(STORAGE_KEY_CURRENCY);
        if (stored && typeof stored === 'string') return stored;
      }
    } catch {
      // ignore localStorage issues
    }
    return 'EUR';
  });

  const setCurrency = async (currencyCode: string) => {
    const next = String(currencyCode || '').trim().toUpperCase();
    if (!next) return;
    setCurrencyState(next);

    // Save to localStorage (always, as fallback/cache)
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_CURRENCY, next);
      }
    } catch (error) {
      console.warn('[i18n] Failed to persist currency preference to localStorage:', error);
    }

    // Save to database if user is logged in
    if (user?.id) {
      try {
        const { error } = await supabase
          .from('users')
          .update({ preferred_currency: next })
          .eq('id', user.id);

        if (error) {
          console.warn('[i18n] Failed to save currency preference to database:', error);
        }
      } catch (error) {
        console.warn('[i18n] Failed to save currency preference to database:', error);
      }
    }
  };

  // Format currency with current locale
  const formatCurrencyWithLocale = (amount: number, currencyOverride?: string) => {
    return formatCurrency(amount, language, currencyOverride || currency);
  };

  // Format number with current locale
  const formatNumberWithLocale = (value: number, options?: Intl.NumberFormatOptions) => {
    return formatNumber(value, language, options);
  };

  // Get month shorts for current locale
  const monthShortsWithLocale = () => {
    return monthShortsUtil(language);
  };

  const value: I18nContextValue = {
    language,
    currency,
    setLanguage,
    setCurrency,
    t,
    formatDate,
    formatRelativeTime: formatRelativeTimeWithLocale,
    formatCurrency: formatCurrencyWithLocale,
    formatNumber: formatNumberWithLocale,
    monthShorts: monthShortsWithLocale,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Hook to access i18n functionality
 */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within a LanguageProvider');
  }
  return context;
}

